import React, { useState, useRef, useContext } from "react";
import { Formik, Field, Form } from "formik";
import {
  Box,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Flex,
  Button,
  Image,
  Grid,
  Icon,
  HStack,
  useToast,
} from "@chakra-ui/react";
import { ProductsContext } from "../../Context/ProductsContext";
import { BiPlus } from "react-icons/bi";
import { BsTrash } from "react-icons/bs";
import * as Yup from "yup";
import CustomField from "../../Common/Form/CustomField";
import { useHistory } from "react-router-dom";
import AddCategory from "./AddCategory";
import AddBrand from "./AddBrand";
import useFetch from "../../../hooks/useFetch";
import { Prompt } from "react-router";
import ConfirmationSwal from "../../../Utils/confirmationSwal";
import { UsersContext } from "../../Context/UsersContext";
import { userIsAllowed } from "../../../Utils/userIsAllowed";

const ProductForm = ({ product, id }) => {
  const hiddenFileInput = useRef(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isNewCategoryOpen, setIsNewCategoryOpen] = useState(false);
  const [isNewBrandOpen, setIsNewBrandOpen] = useState(false);
  const [isSubmitting, setIsSubmiting] = useState(false);
  const {
    brandsOptions,
    categoriesOptions,
    suppliersOptions,
    deleteCategory,
    deleteBrand,
    deleteProduct,
  } = useContext(ProductsContext);
  const { currentUser } = useContext(UsersContext);
  const [isEdited, setIsEdited] = useState(false);
  const history = useHistory();
  const toast = useToast();
  const { post, put } = useFetch();

  const ProductSchema = Yup.object().shape({
    name: Yup.string().required("Ingrese una descripción"),
    cost: Yup.number().required("Ingrese un costo"),
    minStock: Yup.number().required("Ingrese el stock mínimo"),
    stock: Yup.number().required("Ingrese el stock actual"),
    bulk: Yup.number().required("Ingrese el tamaño por bulto"),
    brand: Yup.object().nullable().required("Ingrese una marca"),
    category: Yup.object().nullable().required("Ingrese una categoria"),
  });

  const submitHandler = async (form, isRedirecting) => {
    await form.submitForm();
    if (Object.keys(await form.validateForm()).length !== 0) return false;
    setIsSubmiting(true);
    const value = form.values;
    const formData = new FormData();
    formData.append("image", value.image);
    formData.append("name", value.name);
    formData.append("cost", value.cost);
    formData.append("bulk", value.bulk);
    formData.append("brandId", value.brand.value);
    formData.append("categoryId", value.category.value);
    value.subCategory &&
      formData.append("subCategoryId", value.subCategory.value);
    value.supplier && formData.append("supplierId", value.supplier.value);

    formData.append("stock", value.stock);
    formData.append("minStock", value.minStock);
    formData.append("active", value.active ? 1 : 0);
    formData.append("sale", value.sale ? 1 : 0);

    setIsEdited(false);
    if (id !== "nuevo") {
      put(`/products/${id}`, formData)
        .then(() => {
          toast({
            title: "Artículo editado con éxito.",
            description: "Se editó el artículo correctamente.",
            status: "success",
            duration: 4000,
            isClosable: true,
          });
          isRedirecting && history.push("/articulos");
        })
        .catch((e) => {
          toast({
            title: "Se ha producido un error.",
            description:
              e?.message || "Contacte con el administrador del sistema.",
            status: "error",
            duration: 4000,
            isClosable: true,
          });
        })
        .finally(() => {
          setIsSubmiting(false);
        });
    } else {
      post("/products", formData)
        .then((res) => {
          toast({
            title: "Artículo creado con éxito.",
            description: "Se cargó el artículo correctamente.",
            status: "success",
            duration: 4000,
            isClosable: true,
          });
          if (isRedirecting) {
            history.push("/articulos");
          } else {
            history.replace("/articulos");
            history.push(`/articulos/${res.id}`);
          }
        })
        .catch((e) => {
          toast({
            title: "Se ha producido un error.",
            description:
              e?.message || "Contacte con el administrador del sistema.",
            status: "error",
            duration: 4000,
            isClosable: true,
          });
        })
        .finally(() => {
          setIsSubmiting(false);
        });
    }
  };

  const setupInitialValuess = () => {
    let values = { ...product };
    if (values.brand)
      values.brand = { label: values.brand, value: values.brandId };
    if (values.category)
      values.category = { label: values.category, value: values.categoryId };
    if (values.subCategory)
      values.subCategory = {
        label: values.subCategory,
        value: values.subCategoryId,
      };
    if (values.supplierId)
      values.supplier = {
        label: values.supplierName,
        value: values.supplierId,
      };
    return values;
  };

  const deleteCategoryHandler = (categoryId, setFieldValue) => {
    ConfirmationSwal.fire({
      title: "Eliminar categoría",
      text: "¿Está seguro que desea eliminar la categoría?",
      icon: "question",
      confirmButtonText: "Eliminar",
      cancelButtonText: "Cancelar",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await deleteCategory(categoryId);
          toast({
            title: "Categoría borrada con éxito.",
            status: "success",
            duration: 4000,
            isClosable: true,
          });
          setFieldValue("categories", null);
        } catch (e) {
          toast({
            title: "Se ha producido un error.",
            description: e,
            status: "error",
            duration: 4000,
            isClosable: true,
          });
        }
      }
    });
  };

  const deleteBrandHandler = (brandId, setFieldValue) => {
    ConfirmationSwal.fire({
      title: "Eliminar marca",
      text: "¿Está seguro que desea eliminar la marca?",
      icon: "question",
      confirmButtonText: "Eliminar",
      cancelButtonText: "Cancelar",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await deleteBrand(brandId);
          toast({
            title: "Marca borrada con éxito.",
            status: "success",
            duration: 4000,
            isClosable: true,
          });
          setFieldValue("brand", null);
        } catch (e) {
          toast({
            title: "Se ha producido un error.",
            description: e,
            status: "error",
            duration: 4000,
            isClosable: true,
          });
        }
      }
    });
  };

  const deleteProductHandler = () => {
    ConfirmationSwal.fire({
      title: "Eliminar producto",
      text: "¿Está seguro que desea eliminar el producto?",
      icon: "question",
      confirmButtonText: "Eliminar",
      cancelButtonText: "Cancelar",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await deleteProduct(product.id);
          toast({
            title: "Producto eliminado con éxito.",
            status: "success",
            duration: 4000,
            isClosable: true,
          });
          history.push("/articulos");
        } catch (e) {
          toast({
            title: "Se ha producido un error.",
            description: e,
            status: "error",
            duration: 4000,
            isClosable: true,
          });
        }
      }
    });
  };

  const handleIsEdited = () => {
    setIsEdited(true);
  };

  const handleBlockedNavigation = (nextLocation) => {
    if (isEdited) {
      setIsEdited(false);
      ConfirmationSwal.fire({
        title: "Parece que hay cambios no guardados",
        text: "Desea salir de la página?",
        icon: "question",
        confirmButtonText: "Sí, salir",
        cancelButtonText: "No",
      }).then(async (result) => {
        if (result.isConfirmed) {
          history.push(nextLocation.pathname);
        }
      });
      return false;
    }
    return true;
  };

  return (
    <>
      <Box pb="1rem">
        <Formik
          initialValues={setupInitialValuess()}
          validationSchema={ProductSchema}
          onSubmit={() => {}}
          enableReinitialize={true}
        >
          {(props) => (
            <Form>
              <Grid
                templateColumns={["repeat(1, 1fr)", "repeat(2, 1fr)"]}
                gap={6}
              >
                <Flex justifyContent="flex-start" alignItems="center">
                  <Box w="100%">
                    <HStack>
                      <Box w="100%" mr="1">
                        <CustomField
                          name="brand"
                          title="Marca:"
                          placeholder="Elija una marca"
                          inputType="select"
                          props={props}
                          options={brandsOptions}
                          customStyle={{ textTransform: "uppercase" }}
                        ></CustomField>
                      </Box>
                      <HStack>
                        <Icon
                          as={BiPlus}
                          mx="1"
                          fontSize="1.5em"
                          _hover={{ cursor: "pointer" }}
                          title="Agregar marca"
                          onClick={() => setIsNewBrandOpen(true)}
                        />
                        <Icon
                          as={BsTrash}
                          _hover={{
                            cursor: props.values.brand
                              ? "pointer"
                              : "not-allowed",
                          }}
                          color={props.values.brand ? "inherit" : "grey"}
                          mx="1"
                          fontSize="1.5em"
                          title="Eliminar marca"
                          onClick={
                            props.values.brand
                              ? () =>
                                  deleteBrandHandler(
                                    props.values.brand.value,
                                    props.setFieldValue
                                  )
                              : null
                          }
                        />
                      </HStack>
                    </HStack>
                  </Box>
                </Flex>
                <CustomField
                  name="name"
                  title="Descripción"
                  placeholder="Ingrese un nombre"
                  props={props}
                  customStyle={{ textTransform: "uppercase" }}
                ></CustomField>

                <Flex justifyContent="flex-start" alignItems="center">
                  <Box w="100%">
                    <HStack>
                      <Box w="100%" mr="1">
                        <CustomField
                          name="category"
                          title="Categoría:"
                          placeholder="Elija una categoría"
                          inputType="select"
                          valueLabel="name"
                          props={props}
                          options={categoriesOptions}
                          customStyle={{ textTransform: "uppercase" }}
                        ></CustomField>
                      </Box>
                      <Box>
                        <HStack>
                          <Icon
                            as={BiPlus}
                            mx="1"
                            fontSize="1.5em"
                            _hover={{ cursor: "pointer" }}
                            title="Agregar categoria"
                            onClick={() => setIsNewCategoryOpen(true)}
                          />
                          <Icon
                            as={BsTrash}
                            _hover={{
                              cursor: props.values.category
                                ? "pointer"
                                : "not-allowed",
                            }}
                            color={props.values.category ? "inherit" : "grey"}
                            mx="1"
                            fontSize="1.5em"
                            title="Eliminar marca"
                            onClick={
                              props.values.category
                                ? () =>
                                    deleteCategoryHandler(
                                      props.values.category.value,
                                      props.setFieldValue
                                    )
                                : null
                            }
                          />
                        </HStack>
                      </Box>
                    </HStack>
                  </Box>
                </Flex>
                <Flex justifyContent="flex-start" alignItems="center">
                  <Box w="100%" mr="1">
                    <CustomField
                      name="subCategory"
                      title="Sub-categoría:"
                      placeholder="Elija una subcategoría"
                      inputType="select"
                      valueLabel="name"
                      props={props}
                      options={categoriesOptions}
                      customStyle={{ textTransform: "uppercase" }}
                      setIsEdited={handleIsEdited}
                    ></CustomField>
                  </Box>
                </Flex>
                <CustomField
                  name="cost"
                  title="Costo:"
                  placeholder="Ingrese un monto"
                  type="number"
                  props={props}
                  customStyle={{ textTransform: "uppercase" }}
                  setIsEdited={handleIsEdited}
                ></CustomField>

                <CustomField
                  name="bulk"
                  title="Cantidad por bulto:"
                  placeholder="Ingrese un monto"
                  type="number"
                  props={props}
                  customStyle={{ textTransform: "uppercase" }}
                  setIsEdited={handleIsEdited}
                ></CustomField>
                {userIsAllowed(currentUser, [15]) && (
                  <CustomField
                    name="stock"
                    title="Stock:"
                    placeholder="Ingrese un monto"
                    type="number"
                    props={props}
                    customStyle={{ textTransform: "uppercase" }}
                    setIsEdited={handleIsEdited}
                  ></CustomField>
                )}
                {userIsAllowed(currentUser, [15]) && (
                  <CustomField
                    name="minStock"
                    title="Stock Mínimo:"
                    placeholder="Ingrese un monto"
                    type="number"
                    props={props}
                    customStyle={{ textTransform: "uppercase" }}
                    setIsEdited={handleIsEdited}
                  ></CustomField>
                )}
                <CustomField
                  name="supplier"
                  title="Proveedor:"
                  placeholder="Elija un proveedor"
                  inputType="select"
                  valueLabel="name"
                  props={props}
                  options={suppliersOptions}
                  customStyle={{ textTransform: "uppercase" }}
                ></CustomField>
                <CustomField
                  name="active"
                  title="Activo:"
                  hLabel={true}
                  inputType="switch"
                  props={props}
                  customStyle={{ textTransform: "uppercase" }}
                  setIsEdited={handleIsEdited}
                ></CustomField>
                <CustomField
                  name="sale"
                  title="Oportunidad:"
                  hLabel={true}
                  inputType="switch"
                  props={props}
                  customStyle={{ textTransform: "uppercase" }}
                  setIsEdited={handleIsEdited}
                ></CustomField>
              </Grid>

              <Field name="image">
                {({ field, form }) => (
                  <Flex
                    justifyContent="center"
                    direction="column"
                    alignItems="center"
                  >
                    <FormControl
                      isInvalid={form.errors.image && form.touched.image}
                      textAlign="center"
                      py="1rem"
                    >
                      <FormLabel
                        htmlFor="stock"
                        mb="0"
                        textTransform="uppercase"
                        textAlign="center"
                      >
                        Imagen:
                      </FormLabel>
                      <Button
                        size="sm"
                        variant="outline"
                        loadingText="Uploading..."
                        onClick={() => hiddenFileInput.current.click()}
                      >
                        Cargar Imagen
                      </Button>
                      <input
                        hidden
                        type="file"
                        accept="image/*"
                        ref={hiddenFileInput}
                        onChange={(e) => {
                          if (e.target) {
                            props.setFieldValue("image", e.target.files[0]);
                            setImagePreview(
                              URL.createObjectURL(e.target.files[0])
                            );
                          }
                        }}
                      />

                      <FormErrorMessage>{form.errors.image}</FormErrorMessage>
                    </FormControl>
                    <Box boxSize={["auto", "xs"]} border="black solid 1px">
                      <Image
                        src={
                          imagePreview ??
                          process.env.REACT_APP_IMAGEURL + "/" + product.image
                        }
                        alt="Preview"
                        boxSize={["200px", "300px"]}
                        objectFit="contain"
                        m="auto"
                        height="100%"
                      />
                    </Box>
                  </Flex>
                )}
              </Field>

              <HStack
                justify={["center", "flex-end"]}
                mt="2rem"
                wrap={"wrap"}
                gap="0.5rem"
              >
                <Button
                  variant="ghost"
                  onClick={() => history.push("/articulos")}
                >
                  Cancelar
                </Button>
                <Button
                  colorScheme="blue"
                  isLoading={isSubmitting}
                  onClick={() => submitHandler(props, true)}
                >
                  Guardar
                </Button>
                <Button
                  colorScheme="blue"
                  isLoading={isSubmitting}
                  onClick={() => submitHandler(props, false)}
                >
                  Guardar y seguir editando
                </Button>
                {id !== "nuevo" && (
                  <Button
                    colorScheme="red"
                    onClick={() => deleteProductHandler()}
                  >
                    Eliminar
                  </Button>
                )}
              </HStack>
            </Form>
          )}
        </Formik>
      </Box>
      <AddCategory
        isOpen={isNewCategoryOpen}
        setIsOpen={setIsNewCategoryOpen}
      ></AddCategory>
      <AddBrand
        isOpen={isNewBrandOpen}
        setIsOpen={setIsNewBrandOpen}
      ></AddBrand>
      <Prompt when={isEdited} message={(e) => handleBlockedNavigation(e)} />
    </>
  );
};

export default ProductForm;
